import { useContextBlotterFilter } from '@src/providers/ContextBlotterFilterProvider/useContextBlotterFilter';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  TRANSFER,
  filterByCellValueMenuItem,
  getShowJSONContextItem,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useDateRangeFilter,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterColumnState,
  type FilterClause,
  type FilterableProperty,
  type Transfer,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useClientSideTransfersFilter } from './useClientSideTransfersFilter';
import { useTransfersColumns, type AdditionalTransferColumns } from './useTransfersColumns';
import { useTransfersFilter, type TransfersBlotterFilter } from './useTransfersFilter';

export interface RecentTransfersParams {
  blotterID: string;
  defaultColumns?: BlotterColumnState[];
  defaultFilter?: TransfersBlotterFilter;
  initialIsOpen?: boolean;

  showCloneTab?: boolean;
  onCloneTab: () => void;
  getContextMenuItems?: (params: GetContextMenuItemsParams) => MenuItemDef[];
}

export function FilteredTransfers({
  blotterID,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  showCloneTab,
  onCloneTab,
  getContextMenuItems: getParentContextMenuItems,
}: RecentTransfersParams) {
  const mixpanel = useMixpanel();
  const columns = useTransfersColumns(defaultColumns);

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const { additionalFilterState } = useContextBlotterFilter();
  // only show DateRangePicker if it's not used in the additionalFilterState
  const showDateRangePicker = !(additionalFilterState && 'DateRange' in additionalFilterState);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useTransfersFilter({
    persistedBlotterTable,
    defaultFilter,
  });

  const { clientSideFilter, changeFilter, filterableProperties, initialFilterClauses } = filterResults;

  const clientSideTransfersFilter = useClientSideTransfersFilter(clientSideFilter);
  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<TransfersBlotterFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) as unknown as TransfersBlotterFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const dateRangeFilter = useDateRangeFilter(filterResults.filter, changeFilter, additionalFilterState?.DateRange);

  const { handleClickJson, jsonModal } = useJsonModal();

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    },
  });

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => [
      ...filterByCellValueMenuItem({
        params,
        openClause: filterBuilderAccordion.openClause,
        filterableProperties,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      'separator',
      getShowJSONContextItem({ params, handleClickJson }),
      'separator',
      ...(getParentContextMenuItems?.(params) || []),
      ...getDefaultContextMenuItems(params),
    ],
    [
      filterBuilderAccordion.openClause,
      filterableProperties,
      getDefaultContextMenuItems,
      getParentContextMenuItems,
      mixpanel,
      handleClickJson,
    ]
  );

  const request = useMemo(
    () => ({
      name: TRANSFER,
      tag: 'TransfersBlotter',
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    persistence: persistedBlotterTable,
    columns: columns,
    rowID: 'rowID' satisfies keyof Transfer,
    clientLocalFilter: clientSideTransfersFilter,
    handleClickJson,
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems,
      includeHiddenColumnsInQuickFilter: true,
    },
    exportOptions: {
      name: 'Transfers',
    },
  });

  const handleCloneTab = useCallback(() => {
    onCloneTab();
  }, [onCloneTab]);

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...dateRangeFilter}
        {...blotterTable.blotterTableFiltersProps}
        showDateRangePicker={showDateRangePicker}
        prefix={
          showCloneTab && (
            <Button
              startIcon={IconName.Duplicate}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleCloneTab}
            >
              Clone Tab
            </Button>
          )
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {jsonModal}
    </>
  );
}

function onlyServerFilterKeys(filter: TransfersBlotterFilter | undefined) {
  if (!filter) {
    return filter;
  }

  return {
    StartDate: filter.StartDate,
    EndDate: filter.EndDate,
  } satisfies TransfersBlotterFilter;
}

function colIDToFilterBuilderKey(id: string): keyof TransfersBlotterFilter | undefined {
  switch (id as keyof Transfer | AdditionalTransferColumns) {
    case 'Market':
      return 'Provider';
    case 'Currency':
      return 'Currency';
    case 'Status':
      return 'Status';
    case 'OwnerAccountID':
    case 'sourceAccount':
    case 'destinationAccount':
      return 'Account';
    case 'User':
      return 'Submitter';
    default:
      return undefined;
  }
}
